// Projected-performance numbers shown in the audit report.
// Deterministic, NOT from Gemini — formulaic lift toward "best-in-class" (89).

import { AuditSection, PdpAudit } from "./types";

const BEST_IN_CLASS = 89;

export interface SectionLift {
    label: string;
    current: number;
    max: number;
    projected: number;
    driver: string;
}

const HUMAN_DRIVERS: Record<string, string> = {
    conversionOptimization: "Expert video CTA reinforcement + confidence signals",
    contentQuality: "Expert education layer",
    visualExperience: "Demonstration content",
    socialProof: "Expert validation",
    seoMetadata: "Structured benefit language + expert transcript content",
    technicalPerformance: "Static — included unchanged",
    mobileFirst: "Static — included unchanged",
    competitorBenchmarking: "Expert comparison guidance + differentiation clarity",
};

const AGENTIC_DRIVERS: Record<string, string> = {
    structuredTruth: "Structured benefit explanation",
    semanticClarity: "Expert positioning clarity",
    explainability: "Expert explanation content",
    multimodalGrounding: "Demonstration video",
    executionConfidenceSignals: "Expert validation",
    machineCommerceSignals: "Structured transcripts + machine-readable explanations",
};

// Section keys that get NO lift (technical/mobile remain unchanged in PDF spec).
const STATIC_KEYS = new Set(["technicalPerformance", "mobileFirst"]);

export function parseScore(score: string): { current: number; max: number } {
    const m = (score || "").match(/(-?\d+)\s*\/\s*(\d+)/);
    if (!m) return { current: 0, max: 0 };
    return { current: parseInt(m[1], 10) || 0, max: parseInt(m[2], 10) || 0 };
}

function projectSection(key: string, section: AuditSection): SectionLift {
    const { current, max } = parseScore(section.score);
    let projected: number;
    if (STATIC_KEYS.has(key) || max === 0) {
        projected = current;
    } else {
        // Lift toward 89% of max — matches the example PDF's "best-in-class 90+" framing.
        const target = Math.round(max * (BEST_IN_CLASS / 100));
        projected = Math.max(current, Math.min(max, target));
    }
    return {
        label: key,
        current,
        max,
        projected,
        driver: HUMAN_DRIVERS[key] || AGENTIC_DRIVERS[key] || "",
    };
}

export function projectHumanConversion(
    audit: PdpAudit,
): { rows: SectionLift[]; total: number; projectedTotal: number } {
    const rows = (Object.keys(audit.humanConversion) as Array<
        keyof PdpAudit["humanConversion"]
    >).map((k) => projectSection(k as string, audit.humanConversion[k]));
    const total = rows.reduce((s, r) => s + r.current, 0);
    const projectedTotal = rows.reduce((s, r) => s + r.projected, 0);
    return { rows, total, projectedTotal };
}

export function projectAgenticCommerce(
    audit: PdpAudit,
): { rows: SectionLift[]; total: number; projectedTotal: number } {
    const rows = (Object.keys(audit.agenticCommerce) as Array<
        keyof PdpAudit["agenticCommerce"]
    >).map((k) => projectSection(k as string, audit.agenticCommerce[k]));
    const total = rows.reduce((s, r) => s + r.current, 0);
    const projectedTotal = rows.reduce((s, r) => s + r.projected, 0);
    return { rows, total, projectedTotal };
}

export function projectOverall(audit: PdpAudit): {
    current: number;
    projected: number;
} {
    const human = projectHumanConversion(audit);
    const agentic = projectAgenticCommerce(audit);
    const currentAvg = Math.round(
        (audit.scores.humanConversion + audit.scores.agenticCommerce) / 2,
    );
    const projectedAvg = Math.round(
        (human.projectedTotal + agentic.projectedTotal) / 2,
    );
    return {
        current: audit.scores.overall || currentAvg,
        projected: Math.max(audit.scores.overall || currentAvg, projectedAvg),
    };
}
